"use client";

import Link from "next/link";
import {
  Users,
  MessageSquare,
  Siren,
  ShieldCheck,
  KeyRound,
  Trash2,
  ClipboardX,
  Library,
  Route,
  Clock,
  FolderKanban,
  MoreHorizontal,
  Pencil,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { SortableList, SortRow } from "@/components/SortableList";
import {
  SidebarOrderContext,
  useSidebarDaily,
  useSidebarMoreOpen,
  useSidebarOrder,
} from "@/components/sidebar-order";
import {
  COMING_SOON_ITEMS,
  NAV_ITEMS,
  SHOW_COMING_SOON,
  hasPermission,
  isAdminLike,
  splitDaily,
} from "./nav-config";
import { FadeLabel, NavBadge, SidebarTooltip } from "./parts";
import { SidebarCustomize } from "./SidebarCustomize";
import { SidebarNavGroup, type NavChild } from "./SidebarNavGroup";
import type { SidebarGroups } from "./useSidebarGroups";

function NavRow({
  href,
  label,
  icon: Icon,
  active,
  badge,
  expanded,
}: {
  href: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  badge?: number;
  expanded: boolean;
}) {
  return (
    <div
      className={`group flex w-full items-center rounded-xl text-sm transition-colors ${
        active
          ? "bg-accent/10 font-semibold text-accent dark:bg-neutral-800"
          : "font-medium text-fg-soft hover:bg-surface-muted hover:text-fg"
      } ${expanded ? "" : "h-10 w-10 justify-center"}`}
    >
      <SidebarTooltip label={badge ? `${label} (${badge})` : label} expanded={expanded}>
        <Link
          href={href}
          aria-label={label}
          className={`flex min-w-0 flex-1 items-center ${
            expanded ? "gap-3 py-2.5 pl-3" : "h-10 w-10 justify-center px-0"
          }`}
        >
          <div className="relative flex shrink-0">
            <Icon
              size={18}
              className={`shrink-0 transition-colors ${
                active ? "text-accent" : "text-muted group-hover:text-accent"
              }`}
            />
            {!expanded && badge ? <NavBadge count={badge} expanded={false} /> : null}
          </div>
          <FadeLabel expanded={expanded}>{label}</FadeLabel>
          {expanded && badge ? <NavBadge count={badge} expanded={true} /> : null}
        </Link>
      </SidebarTooltip>
    </div>
  );
}

type Entry = { id: string; label: string; render: () => ReactNode };

export function SidebarNav({
  g,
  order,
  pathname,
  permissions,
  isPending,
  kind,
  badges,
  expanded,
  userId,
}: {
  g: SidebarGroups;
  order: ReturnType<typeof useSidebarOrder>;
  pathname: string;
  permissions: string[];
  isPending?: boolean;
  kind: "MEMBER" | "GUEST";
  badges: Record<string, number>;
  expanded: boolean;
  userId?: string;
}) {
  const admin = isAdminLike(permissions);
  const dailyPins = useSidebarDaily(userId);
  const [showMoreOpen, setShowMoreOpen] = useSidebarMoreOpen(userId);
  const [customizing, setCustomizing] = useState(false);

  const row = (id: string, href: string, label: string, icon: LucideIcon, active: boolean, badge?: number): Entry => ({
    id,
    label,
    render: () => <NavRow href={href} label={label} icon={icon} active={active} badge={badge} expanded={expanded} />,
  });
  const group = (id: string, label: string, icon: LucideIcon, children: NavChild[], active: boolean, headerHref = children[0]?.href): Entry[] =>
    children.length === 0
      ? []
      : [{ id, label, render: () => <SidebarNavGroup headerHref={headerHref} label={label} icon={icon} expanded={expanded} headerActive={active}>{children}</SidebarNavGroup> }];
  const can = (...p: string[]) => hasPermission(permissions, p);

  const allItems: Entry[] = isPending
    ? []
    : [
        ...NAV_ITEMS.filter((item) => hasPermission(permissions, item.required) && (!item.memberOnly || kind !== "GUEST")).map((item) =>
          row(item.href, item.href, item.label, item.icon, item.href === "/?view=dashboard" ? pathname === "/" : pathname.startsWith(item.href)),
        ),
        ...group("g:training", "Training", Route, g.onboardingChildren, g.onboardingActive),
        ...group("g:escalations", "Escalations", Siren, g.escalationsChildren, g.escalationsActive, "/escalations"),
        ...group("g:projects", "Projects", FolderKanban, g.projectsChildren, g.projectsActive, "/projects"),
        ...group("g:bexio", "Time", Clock, g.bexioChildren, g.bexioActive),
        ...(SHOW_COMING_SOON ? COMING_SOON_ITEMS : []).map(({ label, icon, slug }) => row(`soon:${slug}`, `/coming-soon/${slug}`, label, icon, pathname === `/coming-soon/${slug}`)),
        ...group("g:library", "Library", Library, g.libraryChildren, g.libraryActive, "/library/assets"),
        ...(can("users:manage", "users:approve", "users:invite") ? [row("users", "/admin/users", "Users", Users, pathname.startsWith("/admin/users") || pathname === "/admin/guests")] : []),
        ...group("g:access", "Access management", ShieldCheck, g.accessChildren, g.accessActive),
        ...(can("trash:restore") ? [row("trash", "/admin/trash", "Trash", Trash2, pathname.startsWith("/admin/trash"))] : []),
        ...(can("articles:view") ? [row("deletion-requests", "/deletion-requests", "Deletion requests", ClipboardX, pathname.startsWith("/deletion-requests"), badges["/deletion-requests"])] : []),
        ...(can("feedback:create") ? [row("feedback", "/feedback", "Feedback", MessageSquare, pathname.startsWith("/feedback"), badges["/feedback"])] : []),
      ];

  const { daily, more } = splitDaily(allItems, dailyPins.stored, admin);

  return (
    <SidebarOrderContext.Provider value={order}>
      <nav
        onClick={(e) => e.stopPropagation()}
        className="sidebar-nav-scroll flex-1 space-y-1.5 overflow-x-hidden overflow-y-auto py-3 pl-3 pr-2"
      >
        {!isPending && kind === "GUEST" && (
          <SidebarTooltip label="Request access" expanded={expanded}>
            <Link
              href="/request-access"
              aria-label="Request access"
              className={`group flex items-center rounded-xl text-sm ${
                expanded ? "w-full gap-3 px-3 py-2.5" : "h-10 w-10 justify-center px-0"
              } transition-colors ${
                pathname.startsWith("/request-access")
                  ? "bg-accent/10 font-semibold text-accent dark:bg-neutral-800"
                  : "font-medium text-fg-soft hover:bg-surface-muted hover:text-fg"
              }`}
            >
              <KeyRound
                size={18}
                className={`shrink-0 transition-colors ${
                  pathname.startsWith("/request-access")
                    ? "text-accent"
                    : "text-muted group-hover:text-accent"
                }`}
              />
              <FadeLabel expanded={expanded}>Request access</FadeLabel>
            </Link>
          </SidebarTooltip>
        )}

        <SortableList
          className="space-y-1.5"
          order={order.get("top")}
          onOrder={(ids) => order.set("top", ids)}
        >
          {daily.map((item) => (
            <SortRow key={item.id} id={item.id} label={item.label}>
              {item.render()}
            </SortRow>
          ))}
        </SortableList>

        {admin && (
          <div className="group/more flex items-center gap-1 pt-1.5">
            <SidebarTooltip
              label={showMoreOpen ? "Show less" : "Show more"}
              expanded={expanded}
            >
              <button
                type="button"
                onClick={() => setShowMoreOpen((v) => !v)}
                aria-expanded={showMoreOpen}
                aria-label={expanded ? undefined : showMoreOpen ? "Show less" : "Show more"}
                className={`group flex items-center rounded-xl text-sm font-medium text-fg-soft transition-colors hover:bg-surface-muted hover:text-fg ${
                  expanded ? "w-full gap-3 px-3 py-2.5" : "h-10 w-10 justify-center px-0"
                }`}
              >
                <MoreHorizontal
                  size={18}
                  className="shrink-0 text-muted transition-colors group-hover:text-accent"
                />
                <FadeLabel expanded={expanded}>
                  {showMoreOpen ? "Show less" : "Show more"}
                </FadeLabel>
              </button>
            </SidebarTooltip>
            {expanded && (
              <button
                type="button"
                onClick={() => setCustomizing(true)}
                aria-label="Choose what the sidebar shows"
                title="Choose what the sidebar shows"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted opacity-0 transition hover:bg-surface-muted hover:text-accent focus-visible:opacity-100 group-hover/more:opacity-100 [@media(hover:none)]:opacity-100"
              >
                <Pencil size={15} aria-hidden />
              </button>
            )}
          </div>
        )}

        {customizing && (
          <SidebarCustomize
            items={allItems}
            daily={daily}
            onToggle={dailyPins.toggle}
            onReset={() => (dailyPins.reset(), order.reset())}
            onClose={() => setCustomizing(false)}
          />
        )}

        {admin && more.length > 0 && showMoreOpen && (
          <SortableList
            className="space-y-1.5 pt-1.5"
            order={order.get("more")}
            onOrder={(ids) => order.set("more", ids)}
          >
            {more.map((item) => (
              <SortRow key={item.id} id={item.id} label={item.label}>
                {item.render()}
              </SortRow>
            ))}
          </SortableList>
        )}

      </nav>
    </SidebarOrderContext.Provider>
  );
}
